import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    TextField,
    Button,
    Chip,
    Stack,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { callApi } from 'src/utils/apiCall';

const CATEGORY_LABELS = {
    treatment_enquiry: 'Treatment Enquiry',
    hours_query: 'Opening Hours',
    location_query: 'Location',
    appointment_management: 'Appointment',
    existing_patient: 'Existing Patient',
    urgent: 'Urgent',
};

export default function TestDriveStep({ practiceId }) {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [threadId, setThreadId] = useState(null);
    const [suggestedMessages, setSuggestedMessages] = useState([]);
    const [status, setStatus] = useState(null);
    const [repliedCategories, setRepliedCategories] = useState([]);
    const [error, setError] = useState('');
    const [finished, setFinished] = useState(false);
    const pollRef = useRef(null);

    useEffect(() => () => {
        if (pollRef.current) clearInterval(pollRef.current);
    }, []);

    const startPolling = (tid) => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/ai-config/test-drive/status/?thread_id=${tid}`
            );
            if (res.ok) {
                setRepliedCategories(res.data.replied_categories || []);
            }
        }, 10000);
    };

    const handleStart = async () => {
        setStatus('starting');
        setError('');
        const response = await callApi(
            `/api/v1/practices/${practiceId}/ai-config/test-drive/start/`,
            'POST',
            { phone_number: phoneNumber }
        );
        if (!response.ok) {
            setError(response.data?.error || response.data?.detail || 'Failed to start test drive');
            setStatus('error');
            return;
        }
        setThreadId(response.data.thread_id);
        setSuggestedMessages(response.data.suggested_messages || []);
        setStatus('ready');
        startPolling(response.data.thread_id);
    };

    const handleReset = async () => {
        setError('');
        const res = await callApi(
            `/api/v1/practices/${practiceId}/ai-config/test-drive/reset/`,
            'POST',
            { thread_id: threadId }
        );
        if (res.ok) {
            setRepliedCategories([]);
            if (pollRef.current) clearInterval(pollRef.current);
            startPolling(threadId);
        } else {
            setError(res.data?.error || res.data?.detail || 'Failed to reset');
        }
    };

    const handleFinish = async () => {
        if (pollRef.current) clearInterval(pollRef.current);
        // Clean up test drive flag on the thread so it doesn't permanently bypass guards
        if (threadId) {
            await callApi(
                `/api/v1/practices/${practiceId}/ai-config/test-drive/stop/`,
                'POST',
                { thread_id: threadId }
            );
        }
        setFinished(true);
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
    };

    if (finished) {
        return (
            <Box>
                <Typography sx={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: '#00A76F', mb: '10px',
                }}>
                    Test Drive
                </Typography>
                <Typography sx={{
                    fontSize: 22, fontWeight: 800, color: '#0D0D0D',
                    lineHeight: 1.3, letterSpacing: '-0.4px', mb: 1,
                }}>
                    Test complete
                </Typography>
                <Alert severity="success" sx={{ mt: 2, borderRadius: '10px' }}>
                    Test drive complete! Click Continue to proceed to the summary.
                </Alert>
            </Box>
        );
    }

    if (!status || status === 'error') {
        const isThreadError = error && error.toLowerCase().includes('no whatsapp thread');
        return (
            <Box>
                <Typography sx={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: '#00A76F', mb: '10px',
                }}>
                    Test Drive
                </Typography>
                <Typography sx={{
                    fontSize: 22, fontWeight: 800, color: '#0D0D0D',
                    lineHeight: 1.3, letterSpacing: '-0.4px', mb: 1,
                }}>
                    Try your WhatsApp AI configuration
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: '#7A7A7A', lineHeight: 1.6, mb: '20px' }}>
                    Test how AI responds by sending real WhatsApp messages from your phone.
                </Typography>

                {/* How it works steps */}
                <Box sx={{
                    p: 2, mb: '20px', borderRadius: '12px',
                    backgroundColor: '#F8F9FA', border: '1px solid #EBEBEB',
                }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#212B36', mb: 1.5 }}>
                        How it works
                    </Typography>
                    {[
                        { num: '1', text: 'Send a WhatsApp message to your practice number from the phone below' },
                        { num: '2', text: 'Enter your number here and hit Start' },
                        { num: '3', text: 'Send the suggested test messages and watch AI reply' },
                    ].map((step) => (
                        <Box key={step.num} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1, '&:last-child': { mb: 0 } }}>
                            <Box sx={{
                                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                backgroundColor: '#00A76F', color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 11, fontWeight: 700,
                            }}>
                                {step.num}
                            </Box>
                            <Typography sx={{ fontSize: 12.5, color: '#637381', lineHeight: 1.5, pt: '2px' }}>
                                {step.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {error && !isThreadError && (
                    <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: '10px' }}>
                        {error}
                    </Alert>
                )}

                {isThreadError && (
                    <Alert
                        severity="info"
                        icon={<Icon icon="mdi:whatsapp" width={22} color="#25D366" />}
                        onClose={() => setError('')}
                        sx={{ mb: 2, borderRadius: '10px', alignItems: 'center' }}
                    >
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#212B36', mb: 0.5 }}>
                            Send a message first
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#637381' }}>
                            Open WhatsApp on your phone and send any message to your practice number. Then come back and try again.
                        </Typography>
                    </Alert>
                )}

                <TextField
                    fullWidth
                    label="Your WhatsApp number"
                    placeholder="+44 7852 461505"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            fontSize: 13, borderRadius: '10px',
                            '& fieldset': { borderWidth: '1.5px', borderColor: '#EBEBEB' },
                            '&.Mui-focused fieldset': { borderColor: '#00A76F', borderWidth: '1.5px' },
                        },
                    }}
                />

                <Button
                    variant="contained"
                    onClick={handleStart}
                    disabled={!phoneNumber.trim() || status === 'starting'}
                    startIcon={
                        status === 'starting'
                            ? <CircularProgress size={16} color="inherit" />
                            : <Icon icon="mdi:play-circle-outline" width={18} height={18} />
                    }
                    sx={{
                        backgroundColor: '#00A76F',
                        '&:hover': { backgroundColor: '#007A52' },
                        fontWeight: 700, borderRadius: '99px', px: 3,
                        textTransform: 'none', fontSize: 14,
                    }}
                >
                    {status === 'starting' ? 'Starting...' : 'Start Test Drive'}
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            <Typography sx={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#00A76F', mb: '10px',
            }}>
                Test Drive
            </Typography>
            <Typography sx={{
                fontSize: 22, fontWeight: 800, color: '#0D0D0D',
                lineHeight: 1.3, letterSpacing: '-0.4px', mb: 1,
            }}>
                Send these messages to test
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: '#7A7A7A', lineHeight: 1.6, mb: '28px' }}>
                Copy and send these from your WhatsApp. WhatsApp AI will respond automatically.
            </Typography>

            {error && (
                <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: '10px' }}>
                    {error}
                </Alert>
            )}

            <Stack spacing={1}>
                {suggestedMessages.map((msg) => {
                    const replied = repliedCategories.includes(msg.category);
                    return (
                        <Box
                            key={msg.category}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                p: '12px 14px', borderRadius: '12px',
                                border: '1.5px solid',
                                borderColor: replied ? '#00A76F' : '#EBEBEB',
                                backgroundColor: replied ? '#F0FAF6' : '#fff',
                                transition: 'all 0.15s',
                            }}
                        >
                            {replied ? (
                                <Icon icon="mdi:check-circle" width={22} height={22} color="#00A76F" />
                            ) : (
                                <Icon icon="mdi:circle-outline" width={22} height={22} color="#B0B0B0" />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, color: '#0D0D0D', mb: 0.5 }}>
                                    {msg.text}
                                </Typography>
                                <Chip
                                    label={CATEGORY_LABELS[msg.category] || msg.category}
                                    size="small"
                                    sx={{
                                        backgroundColor: 'rgba(0,167,111,0.12)',
                                        color: '#007A52', fontWeight: 600, fontSize: '0.7rem',
                                    }}
                                />
                            </Box>
                            <Tooltip title="Copy message">
                                <IconButton size="small" onClick={() => handleCopy(msg.text)}>
                                    <Icon icon="mdi:content-copy" width={18} height={18} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                })}
            </Stack>

            {repliedCategories.length > 0 && (
                <Typography sx={{ fontSize: 12, color: '#7A7A7A', mt: 1.5 }}>
                    {repliedCategories.length} of {suggestedMessages.length} categories replied
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<Icon icon="mdi:refresh" width={18} height={18} />}
                    sx={{
                        color: '#7A7A7A', borderColor: '#EBEBEB', borderRadius: '99px',
                        textTransform: 'none', fontWeight: 600,
                        '&:hover': { backgroundColor: '#F7F7F7', borderColor: '#EBEBEB' },
                    }}
                >
                    Reset & Test Again
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleFinish}
                    startIcon={<Icon icon="mdi:check" width={18} height={18} />}
                    sx={{
                        color: '#00A76F', borderColor: 'rgba(0,167,111,0.48)', borderRadius: '99px',
                        textTransform: 'none', fontWeight: 600,
                        '&:hover': { backgroundColor: 'rgba(0,167,111,0.08)', borderColor: 'rgba(0,167,111,0.48)' },
                    }}
                >
                    Finish Testing
                </Button>
            </Box>
        </Box>
    );
}

TestDriveStep.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TestDriveStep.defaultProps = {
    practiceId: null,
};
